import PropTypes from 'prop-types';
import Immutable from 'seamless-immutable';
import { retrieveErrors } from '@widergy/web-utils/lib/message';
import Polling from '@widergy/polling-handling';
import { AlertHandler } from '@widergy/energy-ui';

import { message } from '../utils/stringUtils';
import * as VoucherService from '../services/VoucherService';

import { stringArrayToObject } from './utils';
import { actionCreators as pointActions } from './pointHandlers';

/* ------------- Voucher actions ------------- */
export const actions = stringArrayToObject(
  [
    'GET_VOUCHERS',
    'GET_VOUCHERS_SUCCESS',
    'GET_VOUCHERS_FAILURE',
    'LOADING_EXCHANGE',
    'VOUCHER_EXCHANGE_SUCCESS',
    'VOUCHER_EXCHANGE_FAILURE',
    'VOUCHER_EXCHANGE_FINISHED'
  ],
  '@@VOUCHER'
);

const PAGE_NUMBER = 1;
const PAGE_SIZE = 100;

export const actionCreators = {
  loadingExchange(loadingExchange = true) {
    return {
      type: actions.LOADING_EXCHANGE,
      payload: { loadingExchange }
    };
  },
  getVouchers() {
    return {
      type: actions.GET_VOUCHERS
    };
  },
  getVouchersInfo() {
    return async dispatch => {
      dispatch(actionCreators.getVouchers());
      const result = await VoucherService.getVouchers();
      if (result.ok) {
        dispatch(actionCreators.getVouchersSuccess(result.data));
      } else {
        AlertHandler.error(retrieveErrors(result.data, message('DefaultMessages:getVouchersInfoFailure')));
        dispatch(actionCreators.getVouchersFailure(result.data));
      }
    };
  },
  getVouchersSuccess(vouchersInformation) {
    return {
      type: actions.GET_VOUCHERS_SUCCESS,
      payload: {
        vouchers: vouchersInformation
      }
    };
  },
  getVouchersFailure(vouchersError) {
    return {
      type: actions.GET_VOUCHERS_FAILURE,
      payload: {
        error: vouchersError
      }
    };
  },
  voucherExchange(accountId, voucherId) {
    return async dispatch => {
      dispatch(actionCreators.loadingExchange(true));
      const response = await Polling.handle(VoucherService.voucherExchange(accountId, voucherId));

      if (response.error) {
        if (response.status === 422) {
          const errorMessages = {
            'deleted-voucher': message('Vouchers:deletedVoucher'),
            'expired-voucher': message('Vouchers:expiredVoucher'),
            'already-used-voucher': message('Vouchers:alreadyUsedVoucher'),
            'insufficient-points': message('Vouchers:insufficientPoints')
          };
          dispatch(actionCreators.voucherExchangeFailure(`${errorMessages[response.error.error]}` || ''));
        } else {
          dispatch(actionCreators.voucherExchangeFailure(message('DefaultMessages:voucherExchangeFailure')));
        }
      } else {
        const { voucherDescription } = response.data;
        dispatch(actionCreators.voucherExchangeSuccess(voucherDescription));
        dispatch(pointActions.getPointsDetail(PAGE_NUMBER, PAGE_SIZE));
      }
    };
  },
  voucherExchangeSuccess(description) {
    return {
      type: actions.VOUCHER_EXCHANGE_SUCCESS,
      payload: description
    };
  },
  voucherExchangeFailure(errorMessage) {
    return {
      type: actions.VOUCHER_EXCHANGE_FAILURE,
      payload: {
        error: errorMessage
      }
    };
  },
  voucherExchangeFinished() {
    return {
      type: actions.VOUCHER_EXCHANGE_FINISHED
    };
  }
};

/* ------------- Voucher reducer ------------- */
const defaultState = {
  vouchers: [],
  loading: true,
  loadingExchange: false,
  exchangeInProgress: false,
  error: null
};

/* eslint-disable complexity */
export function reducer(state = Immutable(defaultState), action) {
  switch (action.type) {
    case actions.LOADING_EXCHANGE: {
      return Immutable.merge(state, {
        loadingExchange: action.payload,
        exchangeInProgress: true
      });
    }
    case actions.GET_VOUCHERS: {
      return Immutable.merge(state, { vouchers: [], loading: true });
    }
    case actions.GET_VOUCHERS_SUCCESS: {
      return Immutable.merge(state, {
        vouchers: action.payload.vouchers,
        loading: false,
        error: null
      });
    }
    case actions.GET_VOUCHERS_FAILURE: {
      return Immutable.merge(state, {
        error: action.payload.error,
        loading: false
      });
    }
    case actions.VOUCHER_EXCHANGE_SUCCESS: {
      return Immutable.merge(state, {
        loadingExchange: false,
        isExchangeSuccessful: true,
        error: null
      });
    }
    case actions.VOUCHER_EXCHANGE_FAILURE: {
      return Immutable.merge(state, {
        loadingExchange: false,
        isExchangeSuccessful: false,
        error: action.payload.error
      });
    }
    case actions.VOUCHER_EXCHANGE_FINISHED: {
      return Immutable.merge(state, {
        loadingExchange: false,
        isExchangeSuccessful: false,
        exchangeInProgress: false,
        error: null
      });
    }
    default: {
      return state;
    }
  }
}
/* eslint-enable complexity */

/* ------------- Voucher propTypes ------------- */
export const voucherPropTypes = PropTypes.shape({
  id: PropTypes.number.isRequired,
  icon_url: PropTypes.string,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  points: PropTypes.number.isRequired,
  category: PropTypes.oneOf(['product', 'bill']).isRequired,
  discount: PropTypes.number.isRequired,
  discount_type: PropTypes.oneOf(['money', 'percentage']).isRequired,
  price: PropTypes.number
});
